// src/views/admin/ADParametres.jsx
//
// PERMISSIONS (module "parametres") — voir aussi ADAnneesScolaires,
// ADEtablissement et ADConfigClasses qui portent les autres :
// - parametres.voir                    : accès à la page (déjà géré sidebar / route)
// - parametres.modifier_pointage       : onglet "Paramètres pointages" (sinon lecture seule)
// - parametres.gerer_cartes            : onglet "Config. Cartes" (créer / modifier / activer / supprimer)
// - parametres.gerer_bulletins         : onglet "Config. Bulletins" (créer / modifier / activer / supprimer)
// Le bouton "Détails" reste visible pour tous.

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import ADAnneesScolaires from "./ADAnneesScolaires";
import ADEtablissement from "./ADEtablissement";
import ADConfigClasses from "./ADConfigClasses";
import ADSetupChecklist from "./ADSetupChecklist";

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TagButton({ tag, label, onClick }) {
  return (
    <button type="button" className="bsec" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => onClick(tag)}>
      {label}
    </button>
  );
}

function AppreciationEditor({ label, rows, setRows, onLoadDefault }) {
  const addRow = () => setRows(prev => [...prev, { seuil: "", texte: "" }]);
  const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  return (
    <div className="fg" style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <label>{label}</label>
        <button type="button" className="bsec" style={{ padding: "4px 12px", fontSize: 12 }} onClick={onLoadDefault}>
          <i className="ti ti-refresh" /> Valeurs par défaut
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input type="number" step="0.5" placeholder="Seuil (≥)" value={r.seuil}
              onChange={e => updateRow(i, "seuil", e.target.value)} style={{ width: 80 }} />
            <input type="text" placeholder="Appréciation" value={r.texte}
              onChange={e => updateRow(i, "texte", e.target.value)} style={{ flex: 1 }} />
            <button type="button" className="ibtn" onClick={() => removeRow(i)}>
              <i className="ti ti-trash" />
            </button>
          </div>
        ))}
        <button type="button" className="bsec" onClick={addRow} style={{ alignSelf: "flex-start" }}>
          <i className="ti ti-plus" />Ajouter un seuil
        </button>
      </div>
    </div>
  );
}

const dictToRows = (dict) =>
  dict && typeof dict === "object"
    ? Object.entries(dict).map(([seuil, texte]) => ({ seuil, texte }))
    : [];
const rowsToDict = (rows) =>
  rows.reduce((acc, r) => {
    if (r.seuil !== "" && r.texte.trim() !== "") acc[r.seuil] = r.texte.trim();
    return acc;
  }, {});

const CYCLE_LABELS = {
  PRESCOLAIRE: "Préscolaire",
  PRIMAIRE: "Primaire",
  COLLEGE: "Collège",
  LYCEE: "Lycée",
};

const AVAILABLE_TAGS = [
  { label: "Nom élève", tag: "{{ eleve.nom }}" },
  { label: "Prénom élève", tag: "{{ eleve.prenom }}" },
  { label: "Nom parent", tag: "{{ parent.nom }}" },
  { label: "Date pointage", tag: "{{ date }}" },
  { label: "Heure pointage", tag: "{{ heure }}" },
  { label: "Statut présence", tag: "{{ statut }}" },
  { label: "Type pointage", tag: "{{ type_pointage }}" },
  { label: "Motif sortie", tag: "{{ motif }}" }
];

const DEFAULT_PARAMETRES = {
  heure_arrivee_max: "08:00",
  heure_sortie_min: "17:00",
  pause_debut: "12:30",
  pause_fin: "15:00",
  heure_autorisee: "14:30",
  message_entree_present: "L'élève {{ eleve.nom }} {{ eleve.prenom }} est arrivé à {{ heure }}.",
  message_entree_retard: "L'élève {{ eleve.nom }} {{ eleve.prenom }} est en retard à {{ heure }}.",
  message_entree_absent: "L'élève {{ eleve.nom }} {{ eleve.prenom }} est absent.",
  message_sortie: "L'élève {{ eleve.nom }} {{ eleve.prenom }} est sorti à {{ heure }}.",
  message_sortie_exceptionnelle: "L'élève {{ eleve.nom }} {{ eleve.prenom }} est sorti exceptionnellement à {{ heure }} pour le motif : {{ motif }}."
};

// ── Les configs ne portent plus que ce qui varie par année/cycle ──
const EMPTY_CARTE = {
  annee_scolaire: "",
  actif: false,
  coordonnees_carte: "",
};

const EMPTY_BULLETIN = {
  cycle: "",
  annee_scolaire: "",
  note_maximale: "20.00",
  est_actif: true,
};

const VALID_TABS = ["etablissement", "classes", "annees", "parametres", "cartes", "bulletins"];

export default function ADParametres({ slug }) {
  const navigate = useNavigate();
  const { canSee } = usePermissions();

  // Raccourcis de permissions pour garder le JSX lisible
  const peutModifierPointage = canSee("parametres.modifier_pointage");
  const peutGererCartes = canSee("parametres.gerer_cartes");
  const peutGererBulletins = canSee("parametres.gerer_bulletins");

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.includes(initialTab) ? initialTab : "etablissement"
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [annees, setAnnees] = useState([]);
  const [cyclesEtablissement, setCyclesEtablissement] = useState([]);

  const [parametres, setParametres] = useState(DEFAULT_PARAMETRES);

  const [configsCartes, setConfigsCartes] = useState([]);
  const [modalCarte, setModalCarte] = useState(null);
  const [selectedCarte, setSelectedCarte] = useState(null);
  const [formCarte, setFormCarte] = useState(EMPTY_CARTE);

  const [configsBulletins, setConfigsBulletins] = useState([]);
  const [modalBulletin, setModalBulletin] = useState(null);
  const [selectedBulletin, setSelectedBulletin] = useState(null);
  const [formBulletin, setFormBulletin] = useState(EMPTY_BULLETIN);
  const [rowsEducateur, setRowsEducateur] = useState([]);
  const [rowsEcole, setRowsEcole] = useState([]);

  const [detailsModal, setDetailsModal] = useState(null);

  const toast = useToastHook();
  const {
    getAnneesScolaires,
    getEtablissementCourant,
    getParametres, updateParametres,
    getConfigurationsCarte, createConfigurationCarte,
    updateConfigurationCarte, updateConfigurationCartePartial,
    deleteConfigurationCarte,
    getBulletinConfigurations, createBulletinConfiguration,
    updateBulletinConfiguration, updateBulletinConfigurationPartial,
    deleteBulletinConfiguration,
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  // Garde activeTab synchro si le paramètre ?tab= change alors que
  // le composant est déjà monté (navigation depuis la checklist sans remount).
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && VALID_TABS.includes(tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };

  useEffect(() => {
    if (slug && activeTab === "etablissement") fetchEtablissement();
    if (slug && activeTab === "parametres") fetchParametres();
    if (slug && activeTab === "cartes") fetchConfigsCartes();
    if (slug && activeTab === "bulletins") fetchConfigsBulletins();
    if (slug && activeTab === "annees") fetchAnnees();
  }, [slug, activeTab]);

  const fetchEtablissement = async () => {
    try {
      const etabData = await getEtablissementCourant(slug);
      setCyclesEtablissement(etabData.cycles || []);
    } catch (error) {
      toast.error("Impossible de charger les données de l'établissement");
    }
  };

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        fetchEtablissement(),
      ]);
      setAnnees(anneesData);
      await fetchAnnees();
      await fetchParametres();
      await fetchConfigsCartes();
      await fetchConfigsBulletins();
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnees = async () => {
    try {
      const data = await getAnneesScolaires(slug, { show_archived: "true" });
      setAnnees(data);
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    }
  };

  const fetchParametres = async () => {
    try {
      const data = await getParametres(slug);
      setParametres(data || DEFAULT_PARAMETRES);
    } catch {
      setParametres(DEFAULT_PARAMETRES);
    }
  };

  const handleParametresChange = (field, value) => {
    setParametres(prev => ({ ...prev, [field]: value }));
  };

  const insertTag = (field, tag) => {
    setParametres(prev => ({ ...prev, [field]: prev[field] + " " + tag }));
  };

  const handleSubmitParametres = async (e) => {
    e.preventDefault();
    if (!peutModifierPointage) return;
    setSaving(true);
    try {
      await updateParametres(slug, parametres);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des paramètres");
    } finally {
      setSaving(false);
    }
  };

  // ── Configurations Cartes ──────────────────────────────────────────
  const fetchConfigsCartes = async () => {
    try {
      const data = await getConfigurationsCarte(slug);
      setConfigsCartes(data);
    } catch (error) {
      toast.error("Impossible de charger les configurations cartes");
    }
  };

  const openCreateCarte = () => {
    const anneeActive = annees.find(a => a.actif);
    setFormCarte({ ...EMPTY_CARTE, annee_scolaire: String((anneeActive || annees[0])?.id || "") });
    setModalCarte("create");
  };

  const openEditCarte = (c) => {
    setSelectedCarte(c);
    setFormCarte({
      annee_scolaire: c.annee_scolaire || "",
      actif: c.actif,
      coordonnees_carte: c.coordonnees_carte || "",
    });
    setModalCarte("edit");
  };

  const openDeleteCarte = (c) => {
    setSelectedCarte(c);
    setModalCarte("delete");
  };

  const handleCarteChange = (field, value) => {
    setFormCarte(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitCarte = async () => {
    if (!peutGererCartes) return;
    if (!formCarte.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        annee_scolaire: formCarte.annee_scolaire,
        actif: formCarte.actif,
        coordonnees_carte: formCarte.coordonnees_carte?.trim() || "",
      };

      if (modalCarte === "create") {
        const created = await createConfigurationCarte(slug, dataToSend);
        setConfigsCartes(prev => [created, ...prev]);
        toast.success("Configuration créée avec succès");
      } else {
        const updated = await updateConfigurationCarte(slug, selectedCarte.id, dataToSend);
        setConfigsCartes(prev => prev.map(c => c.id === selectedCarte.id ? updated : c));
        toast.success("Configuration modifiée avec succès");
      }
      setModalCarte(null);
      await fetchConfigsCartes();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleCarteActif = async (c) => {
    if (!peutGererCartes) return;
    if (c.actif) return;
    try {
      await updateConfigurationCartePartial(slug, c.id, { actif: true });
      await fetchConfigsCartes();
      toast.success("Configuration activée");
    } catch (error) {
      toast.error("Impossible d'activer cette configuration");
    }
  };

  const handleDeleteCarte = async () => {
    if (!peutGererCartes) return;
    setSaving(true);
    try {
      await deleteConfigurationCarte(slug, selectedCarte.id);
      setConfigsCartes(prev => prev.filter(c => c.id !== selectedCarte.id));
      toast.success("Configuration supprimée");
      setModalCarte(null);
    } catch (error) {
      toast.error("Impossible de supprimer");
    } finally {
      setSaving(false);
    }
  };

  // ── Configurations Bulletins ──────────────────────────────────────
  const fetchConfigsBulletins = async () => {
    try {
      const data = await getBulletinConfigurations(slug);
      setConfigsBulletins(data);
    } catch (error) {
      toast.error("Impossible de charger les configurations bulletins");
    }
  };

  const openCreateBulletin = () => {
    const anneeActive = annees.find(a => a.actif);
    setFormBulletin({
      ...EMPTY_BULLETIN,
      annee_scolaire: String((anneeActive || annees[0])?.id || ""),
      cycle: cyclesEtablissement.length === 1 ? cyclesEtablissement[0] : "",
    });
    setRowsEducateur([]);
    setRowsEcole([]);
    setModalBulletin("create");
  };

  const openEditBulletin = (c) => {
    setSelectedBulletin(c);
    setFormBulletin({
      cycle: c.cycle || "",
      annee_scolaire: c.annee_scolaire || "",
      note_maximale: c.note_maximale ?? "20.00",
      est_actif: c.est_actif,
    });
    setRowsEducateur(dictToRows(c.appreciation_educateur));
    setRowsEcole(dictToRows(c.appreciation_ecole));
    setModalBulletin("edit");
  };

  const openDeleteBulletin = (c) => {
    setSelectedBulletin(c);
    setModalBulletin("delete");
  };

  const handleBulletinChange = (field, value) => {
    setFormBulletin(prev => ({ ...prev, [field]: value }));
  };

  const loadDefaultEducateur = () => {
    setRowsEducateur([
      { seuil: "9", texte: "Excellent travail !" },
      { seuil: "8", texte: "Très bon travail !" },
      { seuil: "7", texte: "Bon travail !" },
      { seuil: "6", texte: "Travail satisfaisant" },
      { seuil: "5", texte: "Peut mieux faire" },
      { seuil: "0", texte: "Doit redoubler d'efforts" }
    ]);
  };

  const loadDefaultEcole = () => {
    setRowsEcole([
      { seuil: "8", texte: "Félicitations pour ces excellents résultats !" },
      { seuil: "7", texte: "Votre enfant est autonome dans les tâches proposées." },
      { seuil: "6", texte: "Bon travail, continuez ainsi." },
      { seuil: "5", texte: "Des efforts sont encore nécessaires." },
      { seuil: "0", texte: "Votre enfant réalise les tâches avec une aide soutenue." }
    ]);
  };

  const handleSubmitBulletin = async () => {
    if (!peutGererBulletins) return;
    if (!formBulletin.cycle) { toast.error("Le cycle est requis"); return; }
    if (!formBulletin.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        cycle: formBulletin.cycle,
        annee_scolaire: formBulletin.annee_scolaire,
        note_maximale: formBulletin.note_maximale || "20.00",
        est_actif: formBulletin.est_actif,
        appreciation_educateur: rowsToDict(rowsEducateur),
        appreciation_ecole: rowsToDict(rowsEcole),
      };

      if (modalBulletin === "create") {
        const created = await createBulletinConfiguration(slug, dataToSend);
        setConfigsBulletins(prev => [created, ...prev]);
        toast.success("Configuration créée avec succès");
      } else {
        const updated = await updateBulletinConfiguration(slug, selectedBulletin.id, dataToSend);
        setConfigsBulletins(prev => prev.map(c => c.id === selectedBulletin.id ? updated : c));
        toast.success("Configuration modifiée avec succès");
      }
      setModalBulletin(null);
      await fetchConfigsBulletins();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBulletinActif = async (c) => {
    if (!peutGererBulletins) return;
    if (c.est_actif) return;
    try {
      await updateBulletinConfigurationPartial(slug, c.id, { est_actif: true });
      await fetchConfigsBulletins();
      toast.success("Configuration activée");
    } catch (error) {
      toast.error("Impossible d'activer cette configuration");
    }
  };

  const handleDeleteBulletin = async () => {
    if (!peutGererBulletins) return;
    setSaving(true);
    try {
      await deleteBulletinConfiguration(slug, selectedBulletin.id);
      setConfigsBulletins(prev => prev.filter(c => c.id !== selectedBulletin.id));
      toast.success("Configuration supprimée");
      setModalBulletin(null);
    } catch (error) {
      toast.error("Impossible de supprimer");
    } finally {
      setSaving(false);
    }
  };

  const cycleLabel = (val) => CYCLE_LABELS[val] || val;

  if (loading) return <Loader />;

  return (
    <div>
      <ADSetupChecklist
        slug={slug}
        onNavigateTab={(tab) => navigate(`/${slug}/admin/parametres?tab=${tab}`)}
      />

<div className="tab-nav">
  <button className={`tab-btn ${activeTab === "etablissement" ? "active" : ""}`} onClick={() => changeTab("etablissement")}>
    <i className="ti ti-building" /> <span>ÉTABLISSEMENT</span>
  </button>
  <button className={`tab-btn ${activeTab === "classes" ? "active" : ""}`} onClick={() => changeTab("classes")}>
    <i className="ti ti-school" /> <span>CLASSES</span>
  </button>
  <button className={`tab-btn ${activeTab === "annees" ? "active" : ""}`} onClick={() => changeTab("annees")}>
    <i className="ti ti-calendar" /> <span>ANNÉES SCOLAIRES</span>
  </button>
  <button className={`tab-btn ${activeTab === "parametres" ? "active" : ""}`} onClick={() => changeTab("parametres")}>
    <i className="ti ti-clock" /> <span>PARAMÈTRES POINTAGES</span>
  </button>
  <button className={`tab-btn ${activeTab === "cartes" ? "active" : ""}`} onClick={() => changeTab("cartes")}>
    <i className="ti ti-id-badge" /> <span>CONFIG. CARTES</span>
  </button>
  <button className={`tab-btn ${activeTab === "bulletins" ? "active" : ""}`} onClick={() => changeTab("bulletins")}>
    <i className="ti ti-file-certificate" /> <span>CONFIG. BULLETINS</span>
  </button>
</div>


      {activeTab === "etablissement" && <ADEtablissement slug={slug} section="infos" hideNav />}
      {activeTab === "classes" && <ADConfigClasses slug={slug} />}
      {activeTab === "annees" && <ADAnneesScolaires slug={slug} />}

      {/* ─── TAB PARAMÈTRES POINTAGE — parametres.modifier_pointage ─── */}
      {activeTab === "parametres" && (
        <div className="card">
          <form onSubmit={handleSubmitParametres}>
            {/* Sans permission : tous les champs sont désactivés (lecture seule) */}
            <fieldset disabled={!peutModifierPointage} style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}>
              <div className="fgrid" style={{ marginBottom: 20 }}>
                <div className="fg">
                  <label>Heure limite d'arrivée matin</label>
                  <input type="time" value={parametres.heure_arrivee_max || ""} onChange={e => handleParametresChange('heure_arrivee_max', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Heure minimale de sortie</label>
                  <input type="time" value={parametres.heure_sortie_min || ""} onChange={e => handleParametresChange('heure_sortie_min', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Début pause déjeuner</label>
                  <input type="time" value={parametres.pause_debut || ""} onChange={e => handleParametresChange('pause_debut', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Fin pause déjeuner</label>
                  <input type="time" value={parametres.pause_fin || ""} onChange={e => handleParametresChange('pause_fin', e.target.value)} />
                </div>
                <div className="fg">
                  <label>Heure autorisée avant fin pause</label>
                  <input type="time" value={parametres.heure_autorisee || ""} onChange={e => handleParametresChange('heure_autorisee', e.target.value)} />
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                {peutModifierPointage && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
                    <span style={{ fontSize: 14, color: "var(--m)", marginRight: 6 }}>Tags :</span>
                    {AVAILABLE_TAGS.map(t => (
                      <TagButton key={t.tag} tag={t.tag} label={t.label} onClick={insertTag} />
                    ))}
                  </div>
                )}

                {[
                  { label: "Message présence normale", field: "message_entree_present" },
                  { label: "Message retard", field: "message_entree_retard" },
                  { label: "Message absence", field: "message_entree_absent" },
                  { label: "Message sortie normale", field: "message_sortie" },
                  { label: "Message sortie exceptionnelle", field: "message_sortie_exceptionnelle" },
                ].map(({ label, field }) => (
                  <div className="fg" key={field} style={{ marginBottom: 10 }}>
                    <label>{label}</label>
                    <textarea value={parametres[field] || ""} onChange={e => handleParametresChange(field, e.target.value)} rows={2} />
                  </div>
                ))}
              </div>
            </fieldset>

            {peutModifierPointage ? (
              <div className="factions">
                <button type="submit" className="bprim" disabled={saving}>
                  {saving ? "En cours..." : <><i className="ti ti-device-floppy" /> Enregistrer</>}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--h)" }}>
                <i className="ti ti-lock" /> Lecture seule : vous n'avez pas la permission de modifier ces paramètres.
              </div>
            )}
          </form>
        </div>
      )}

      {/* ─── TAB CONFIGURATIONS CARTES — parametres.gerer_cartes ─── */}
      {activeTab === "cartes" && (
        <div>
          <div className="sb2">
            <div style={{ flex: 1 }} />
            <button className="bsec" onClick={fetchConfigsCartes}><i className="ti ti-refresh" />Actualiser</button>
            {peutGererCartes && (
              <button className="bprim" onClick={openCreateCarte}>
                <i className="ti ti-plus" />Nouvelle configuration
              </button>
            )}
          </div>

          <div style={{ fontSize: 13, color: "var(--h)", marginTop: 8 }}>
            Une seule configuration peut être active à la fois pour l'établissement. Le nom, le logo et le pays affichés
            sur la carte viennent des informations de l'établissement (onglet « Établissement »).
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 10 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Année scolaire</th>
                  <th>Établissement</th>
                  <th>Actif</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configsCartes.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.annee_scolaire_nom}</td>
                    <td>{c.etablissement_detail?.nom || <span style={{ color: "var(--h)" }}>—</span>}</td>
                    <td>
                      {c.actif ? (
                        <span className="pill ok">Active</span>
                      ) : peutGererCartes ? (
                        <button className="pill neu" style={{ border: "none", cursor: "pointer" }} onClick={() => handleToggleCarteActif(c)}>
                          Inactive — activer
                        </button>
                      ) : (
                        <span className="pill neu">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="act">
                        {/* Détails : visible pour tous */}
                        <button title="Détails" onClick={() => setDetailsModal({ type: 'carte', data: c })}
                          style={{ background: "#17a2b8", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                          <i className="ti ti-eye" />
                        </button>
                        {peutGererCartes && (
                          <>
                            <button title="Modifier" onClick={() => openEditCarte(c)}
                              style={{ background: "#ffc107", color: "black" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                              <i className="ti ti-edit" />
                            </button>
                            <button className="del" title="Supprimer" onClick={() => openDeleteCarte(c)}>
                              <i className="ti ti-trash" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {configsCartes.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    Aucune configuration trouvée
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB CONFIGURATIONS BULLETINS — parametres.gerer_bulletins ─── */}
      {activeTab === "bulletins" && (
        <div>
          <div className="sb2">
            <div style={{ flex: 1 }} />
            <button className="bsec" onClick={fetchConfigsBulletins}><i className="ti ti-refresh" />Actualiser</button>
            {peutGererBulletins && (
              <button className="bprim" onClick={openCreateBulletin} disabled={cyclesEtablissement.length === 0}>
                <i className="ti ti-plus" />Nouvelle configuration
              </button>
            )}
          </div>

          {cyclesEtablissement.length === 0 && (
            <div style={{ fontSize: 13, color: "var(--h)", marginTop: 10 }}>
              Aucun cycle configuré pour cet établissement.
            </div>
          )}

          <div style={{ fontSize: 13, color: "var(--h)", marginTop: 8 }}>
            Le slogan, l'adresse, les logos et autres infos d'en-tête viennent des informations de l'établissement
            (onglet « Établissement »). Ici, vous ne configurez que le barème et les appréciations propres à ce cycle/année.
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden", marginTop: 10 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Année scolaire</th>
                  <th>Note max</th>
                  <th>Actif</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configsBulletins.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{cycleLabel(c.cycle)}</td>
                    <td>{c.annee_scolaire_nom}</td>
                    <td>{c.note_maximale}</td>
                    <td>
                      {c.est_actif ? (
                        <span className="pill ok">Active</span>
                      ) : peutGererBulletins ? (
                        <button className="pill neu" style={{ border: "none", cursor: "pointer" }} onClick={() => handleToggleBulletinActif(c)}>
                          Inactive — activer
                        </button>
                      ) : (
                        <span className="pill neu">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="act">
                        {/* Détails : visible pour tous */}
                        <button title="Détails" onClick={() => setDetailsModal({ type: 'bulletin', data: c })}
                          style={{ background: "#17a2b8", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                          <i className="ti ti-eye" />
                        </button>
                        {peutGererBulletins && (
                          <>
                            <button title="Modifier" onClick={() => openEditBulletin(c)}
                              style={{ background: "#ffc107", color: "black" }}
                              onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                              <i className="ti ti-edit" />
                            </button>
                            <button className="del" title="Supprimer" onClick={() => openDeleteBulletin(c)}>
                              <i className="ti ti-trash" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {configsBulletins.length === 0 && (
                  <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                    Aucune configuration trouvée
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── MODAL CARTE (allégé) ─── */}
      {peutGererCartes && (modalCarte === "create" || modalCarte === "edit") && (
        <Modal title={modalCarte === "create" ? "Nouvelle configuration carte" : "Modifier la configuration"} onClose={() => setModalCarte(null)} maxWidth={480}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Année scolaire *</label>
              <select value={formCarte.annee_scolaire} onChange={e => handleCarteChange('annee_scolaire', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>

            <div className="fg">
              <label>Coordonnées à afficher sur la carte</label>
              <input
                type="text"
                value={formCarte.coordonnees_carte}
                onChange={e => handleCarteChange("coordonnees_carte", e.target.value)}
                placeholder="Ex. : BOBO-DIOULASSO • Tél : 88 99 88 99"
                maxLength={150}
              />
              <div style={{ fontSize: 12, color: "var(--h)", marginTop: 5 }}>
                <i className="ti ti-info-circle" /> Ces informations seront affichées
                sur la carte scolaire pour identifier et contacter l'établissement.
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={formCarte.actif} onChange={e => handleCarteChange('actif', e.target.checked)} />
              Configuration active
            </label>
            <div style={{ fontSize: 12, color: "var(--h)" }}>
              <i className="ti ti-info-circle" /> Le nom, le logo, le cachet et le pays affichés sur la carte viennent
              des informations de l'établissement. Modifiez-les dans l'onglet « Établissement ».
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalCarte(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSubmitCarte} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modalCarte === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGererCartes && modalCarte === "delete" && selectedCarte && (
        <Modal title="Confirmer la suppression" onClose={() => setModalCarte(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer la configuration <strong>{selectedCarte.annee_scolaire_nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalCarte(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteCarte} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── MODAL BULLETIN (allégé) ─── */}
      {peutGererBulletins && (modalBulletin === "create" || modalBulletin === "edit") && (
        <Modal title={modalBulletin === "create" ? "Nouvelle configuration bulletin" : "Modifier la configuration"} onClose={() => setModalBulletin(null)} maxWidth={560}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9, maxHeight: "70vh", overflowY: "auto", paddingRight: 4 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Cycle *</label>
                <select value={formBulletin.cycle} onChange={e => handleBulletinChange('cycle', e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {cyclesEtablissement.map(c => <option key={c} value={c}>{CYCLE_LABELS[c] || c}</option>)}
                </select>
              </div>
              <div className="fg">
                <label>Année scolaire *</label>
                <select value={formBulletin.annee_scolaire} onChange={e => handleBulletinChange('annee_scolaire', e.target.value)}>
                  <option value="">-- Sélectionner --</option>
                  {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                </select>
              </div>
            </div>

            <div className="fg">
              <label>Note maximale</label>
              <input type="number" step="0.01" value={formBulletin.note_maximale} onChange={e => handleBulletinChange('note_maximale', e.target.value)} style={{ width: 120 }} />
            </div>

            <div style={{ borderTop: "0.5px solid var(--b)", paddingTop: 10 }}>
              <AppreciationEditor
                label="Appréciation éducateur (seuil ≥ note → texte)"
                rows={rowsEducateur}
                setRows={setRowsEducateur}
                onLoadDefault={loadDefaultEducateur}
              />
            </div>
            <div style={{ borderTop: "0.5px solid var(--b)", paddingTop: 10 }}>
              <AppreciationEditor
                label="Appréciation école (seuil ≥ note → texte)"
                rows={rowsEcole}
                setRows={setRowsEcole}
                onLoadDefault={loadDefaultEcole}
              />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
              <input type="checkbox" checked={formBulletin.est_actif} onChange={e => handleBulletinChange('est_actif', e.target.checked)} />
              Configuration active
            </label>

            <div style={{ fontSize: 12, color: "var(--h)" }}>
              <i className="ti ti-info-circle" /> Le slogan, l'adresse, les logos et le nom de l'école viennent des
              informations de l'établissement. Modifiez-les dans l'onglet « Établissement ».
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalBulletin(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSubmitBulletin} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modalBulletin === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGererBulletins && modalBulletin === "delete" && selectedBulletin && (
        <Modal title="Confirmer la suppression" onClose={() => setModalBulletin(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer la configuration <strong>{cycleLabel(selectedBulletin.cycle)} — {selectedBulletin.annee_scolaire_nom}</strong> ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModalBulletin(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteBulletin} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* ─── MODAL DÉTAILS (lit désormais etablissement_detail) ─── */}
      {detailsModal && (
        <Modal title={detailsModal.type === "carte" ? "Détails configuration carte" : "Détails configuration bulletin"} onClose={() => setDetailsModal(null)} maxWidth={700}>
          {detailsModal.type === "carte" ? (
            <div>
              <div className="fgrid">
                <DetailItem label="Année scolaire" value={detailsModal.data.annee_scolaire_nom} />
                <DetailItem label="Nom école" value={detailsModal.data.etablissement_detail?.nom} />
                <DetailItem
                  label="Coordonnées sur la carte"
                  value={detailsModal.data.coordonnees_carte || "—"}
                />
                <DetailItem label="Pays" value={detailsModal.data.etablissement_detail?.pays || "—"} />
                <DetailItem label="Devise" value={detailsModal.data.etablissement_detail?.devise_pays || "—"} />
                <DetailItem label="Statut" value={detailsModal.data.actif ? "✅ Active" : "Inactive"} />
              </div>
              {detailsModal.data.etablissement_detail?.logo && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Logo</label>
                  <img src={detailsModal.data.etablissement_detail.logo} alt="Logo" style={{ maxHeight: 150, borderRadius: 4 }} />
                </div>
              )}
              {detailsModal.data.etablissement_detail?.cachet && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Cachet</label>
                  <img src={detailsModal.data.etablissement_detail.cachet} alt="Cachet" style={{ maxHeight: 150, borderRadius: 4 }} />
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="fgrid">
                <DetailItem label="Cycle" value={cycleLabel(detailsModal.data.cycle)} />
                <DetailItem label="Année scolaire" value={detailsModal.data.annee_scolaire_nom} />
                <DetailItem label="Note maximale" value={detailsModal.data.note_maximale} />
                <DetailItem label="Statut" value={detailsModal.data.est_actif ? "✅ Active" : "Inactive"} />
                <DetailItem label="Slogan" value={detailsModal.data.etablissement_detail?.slogan || "—"} />
                <DetailItem label="Devise école" value={detailsModal.data.etablissement_detail?.devise_ecole || "—"} />
              </div>
              <div className="fg" style={{ marginTop: 10 }}>
                <label>Adresse</label>
                <div style={{ padding: 8, background: "var(--s2)", borderRadius: 4 }}>{detailsModal.data.etablissement_detail?.adresse || "—"}</div>
              </div>
              <div className="fgrid" style={{ marginTop: 10 }}>
                <DetailItem label="Site web" value={detailsModal.data.etablissement_detail?.site_web || "—"} />
                <DetailItem label="Email" value={detailsModal.data.etablissement_detail?.email || "—"} />
                <DetailItem label="Pays" value={detailsModal.data.etablissement_detail?.pays || "—"} />
                <DetailItem label="Devise pays" value={detailsModal.data.etablissement_detail?.devise_pays || "—"} />
              </div>
              {detailsModal.data.etablissement_detail?.logo && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Logo école</label>
                  <img src={detailsModal.data.etablissement_detail.logo} alt="Logo école" style={{ maxHeight: 150, borderRadius: 4 }} />
                </div>
              )}
              {detailsModal.data.etablissement_detail?.logo_pays && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Logo pays</label>
                  <img src={detailsModal.data.etablissement_detail.logo_pays} alt="Logo pays" style={{ maxHeight: 150, borderRadius: 4 }} />
                </div>
              )}
              {detailsModal.data.appreciation_educateur && Object.keys(detailsModal.data.appreciation_educateur).length > 0 && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Appréciations éducateur</label>
                  <div style={{ padding: 8, background: "var(--s2)", borderRadius: 4 }}>
                    {Object.entries(detailsModal.data.appreciation_educateur)
                      .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                      .map(([seuil, texte]) => (
                        <div key={seuil} style={{ padding: 4 }}>≥ {seuil} : {texte}</div>
                      ))}
                  </div>
                </div>
              )}
              {detailsModal.data.appreciation_ecole && Object.keys(detailsModal.data.appreciation_ecole).length > 0 && (
                <div className="fg" style={{ marginTop: 10 }}>
                  <label>Appréciations école</label>
                  <div style={{ padding: 8, background: "var(--s2)", borderRadius: 4 }}>
                    {Object.entries(detailsModal.data.appreciation_ecole)
                      .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                      .map(([seuil, texte]) => (
                        <div key={seuil} style={{ padding: 4 }}>≥ {seuil} : {texte}</div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      )}

      <style>{`
        /* ─── ONGLETS (thème bleu + or) ─── */
        .tab-nav {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
          border-radius: 10px;
          border-bottom: 3px solid var(--or);
          padding: 8px 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 18px;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: rgba(255,255,255,0.75);
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .tab-btn i { font-size: 18px; }

        .tab-btn:hover {
          background: rgba(255,255,255,0.12);
          color: var(--or-lt);
        }

        .tab-btn.active {
          background: white;
          color: var(--pr);
          box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
        }
        .tab-btn.active i { color: var(--or); }
        .tab-btn.active:hover { background: white; color: var(--pr); }

        .act button { width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: all .2s; }
        .act button:hover { opacity: 0.8 !important; }
        .act button.del { background: var(--rd); color: white; }
        .act button.del:hover { background: var(--rd-dk); }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="fg">
      <label>{label}</label>
      <div style={{ padding: "8px 10px", background: "var(--s2)", borderRadius: 4, minHeight: 36, display: "flex", alignItems: "center" }}>
        {value || <span style={{ color: "var(--h)" }}>—</span>}
      </div>
    </div>
  );
}